/**
 * I costi che tornano ogni mese senza reinserirli.
 *
 * È la parte del P&L che nessun gestionale conosce: le utenze arrivano via
 * email, il condominio via bonifico, l'assicurazione una volta l'anno. Si
 * dicono una volta e da lì entrano da sole in ogni mese.
 *
 * Si scrive l'importo **come lo si conosce** — "120 ogni due mesi", non "60 al
 * mese" — e la quota mensile la fa Otto. Chiedere all'host di dividere per due
 * è il genere di attrito per cui un modulo di contabilità non si compila mai.
 */

import { useEffect, useState } from "react";
import { Plus, Repeat, X } from "lucide-react";
import {
  ETICHETTE_CATEGORIA,
  ETICHETTE_PERIODICITA,
  PERIODICITA,
  formatEur,
  quotaMensile,
  todayInRome,
  type CategoriaCosto,
  type CostoRicorrenteView,
  type Periodicita,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { useStore } from "../lib/store.tsx";
import { Card, IconButton, Pill, Skeleton } from "../ui/primitives.tsx";

/**
 * Le categorie che ha senso ripetere.
 *
 * Fuori restano `canone` e `gestione`, che il rapporto già gestisce come
 * passaggi di denaro fra le parti — inserirli anche qui li farebbe sparire dal
 * conto invece di raddoppiarli, che è peggio — e le pulizie, che seguono le
 * partenze e non il calendario.
 */
const CATEGORIE: CategoriaCosto[] = [
  "utenze",
  "condominio",
  "assicurazione",
  "biancheria",
  "manutenzione",
  "altro",
];

export function RicorrentiCard({
  listingId,
  onCambiato,
}: {
  listingId: string;
  onCambiato: () => void;
}) {
  const store = useStore();
  const [voci, setVoci] = useState<CostoRicorrenteView[] | null>(null);
  const [apertoIlForm, setApertoIlForm] = useState(false);

  const ricarica = () =>
    api
      .ricorrenti(listingId)
      .then(({ ricorrenti }) => setVoci(ricorrenti))
      .catch(() => setVoci([]));

  useEffect(() => {
    setVoci(null);
    void ricarica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const chiudi = async (voce: CostoRicorrenteView) => {
    try {
      await api.chiudiRicorrente(listingId, voce.id);
      store.notify(
        `"${voce.descrizione}" non conta più dai prossimi mesi. Quelli passati restano come sono.`,
      );
      await ricarica();
      onCambiato();
    } catch {
      store.notify("Non sono riuscito a chiuderlo.");
    }
  };

  // Un ricorrente chiuso resta nel database perché i mesi passati devono
  // continuare a tornare, ma nell'elenco di oggi non serve più.
  const attivi = (voci ?? []).filter(
    (v) => v.al === null || v.al >= todayInRome(),
  );

  return (
    <Card className="flex h-full flex-col p-4">
      <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-bold text-ink">
        <Repeat size={14} className="text-accent" />
        Costi che si ripetono
      </h3>
      <p className="mb-2.5 text-[13px] leading-relaxed text-ink-soft">
        Utenze, condominio, assicurazione. Si dicono una volta e da lì entrano
        in ogni mese da soli.
      </p>

      {voci === null ? (
        <Skeleton className="h-16" />
      ) : attivi.length === 0 ? (
        <p className="text-[13px] text-ink-faint">
          Nessuno ancora. Sono i costi che il gestionale non vede.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {attivi.map((voce) => (
            <li
              key={voce.id}
              className="flex items-center justify-between gap-2 rounded-field bg-surface-muted px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-ink">
                  {voce.descrizione}
                </p>
                <p className="text-[12px] text-ink-faint">
                  {ETICHETTE_CATEGORIA[voce.categoria]} ·{" "}
                  {formatEur(voce.importoCents)}{" "}
                  {ETICHETTE_PERIODICITA[voce.periodicita]}
                  {voce.periodicita !== "mensile" ? (
                    <>
                      {" · "}
                      <b className="font-semibold text-ink-soft">
                        {formatEur(
                          quotaMensile(voce.importoCents, voce.periodicita),
                        )}{" "}
                        al mese
                      </b>
                    </>
                  ) : null}
                </p>
              </div>
              <IconButton
                label={`Chiudi ${voce.descrizione}`}
                onClick={() => void chiudi(voce)}
              >
                <X size={15} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {apertoIlForm ? (
        <FormRicorrente
          listingId={listingId}
          onAnnulla={() => setApertoIlForm(false)}
          onCreato={async () => {
            setApertoIlForm(false);
            await ricarica();
            onCambiato();
          }}
        />
      ) : (
        <div className="mt-2.5">
          <Pill
            variant="secondary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setApertoIlForm(true)}
          >
            Aggiungi
          </Pill>
        </div>
      )}
    </Card>
  );
}

function FormRicorrente({
  listingId,
  onCreato,
  onAnnulla,
}: {
  listingId: string;
  onCreato: () => void | Promise<void>;
  onAnnulla: () => void;
}) {
  const store = useStore();
  const [categoria, setCategoria] = useState<CategoriaCosto>("utenze");
  const [descrizione, setDescrizione] = useState("");
  const [importo, setImporto] = useState("");
  const [periodicita, setPeriodicita] = useState<Periodicita>("bimestrale");
  const [dal, setDal] = useState(() => todayInRome().slice(0, 7));
  const [salvando, setSalvando] = useState(false);

  const salva = async () => {
    const pulito = importo.trim().replace(/\./g, "").replace(",", ".");
    const cents = Math.round(Number(pulito) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      store.notify("L'importo non è un numero valido.");
      return;
    }
    if (descrizione.trim() === "") {
      store.notify("Dagli un nome, così lo riconosci fra sei mesi.");
      return;
    }

    setSalvando(true);
    try {
      await api.creaRicorrente(listingId, {
        categoria,
        descrizione: descrizione.trim(),
        importoCents: cents,
        periodicita,
        // Vale dal primo del mese scelto: la competenza è mensile, non giornaliera.
        dal: `${dal}-01`,
        al: null,
      });
      store.notify(
        `Segnato: ${formatEur(quotaMensile(cents, periodicita))} al mese, da adesso in poi da solo.`,
      );
      await onCreato();
    } catch (errore) {
      store.notify(
        errore instanceof Error
          ? errore.message
          : "Non sono riuscito a salvarlo.",
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mt-2.5 flex flex-col gap-2 rounded-field bg-surface-muted p-3">
      <div className="flex gap-2">
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Che cos'è
          </span>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as CategoriaCosto)}
            className="rounded-field bg-surface px-2.5 py-2 text-[14px] text-ink outline-none"
          >
            {CATEGORIE.map((c) => (
              <option key={c} value={c}>
                {ETICHETTE_CATEGORIA[c]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-[1.4] flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Come lo chiami
          </span>
          <input
            type="text"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            placeholder="Luce e gas"
            className="rounded-field bg-surface px-2.5 py-2 text-[14px] text-ink outline-none placeholder:text-ink-faint"
          />
        </label>
      </div>

      {/*
        I tre campi non valgono un terzo ciascuno.
        
        Un `<select>` e un `<input type="month">` hanno una larghezza minima
        loro — «September 2026» più l'icona del calendario — e con `flex-1` e
        basta non si stringono: schiacciano l'unico campo libero della riga,
        quello dell'importo, fino a nascondere quello che ci si sta scrivendo
        dentro. Qui ognuno dichiara la larghezza sotto cui non vuole scendere
        e la riga va a capo invece di comprimere: in una colonna stretta si
        vedono tutti e tre, solo su due righe.
      */}
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-[104px] flex-[0.7] flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Quanto
          </span>
          <span className="flex items-center gap-1.5 rounded-field bg-surface px-2.5">
            <span className="text-sm text-ink-faint">€</span>
            <input
              type="text"
              inputMode="decimal"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
              placeholder="120"
              className="w-full min-w-0 bg-transparent py-2 text-[14px] tabular-nums text-ink outline-none placeholder:text-ink-faint"
            />
          </span>
        </label>

        <label className="flex min-w-[132px] flex-1 flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Ogni quanto
          </span>
          <select
            value={periodicita}
            onChange={(e) => setPeriodicita(e.target.value as Periodicita)}
            className="min-w-0 rounded-field bg-surface px-2.5 py-2 text-[14px] text-ink outline-none"
          >
            {PERIODICITA.map((p) => (
              <option key={p} value={p}>
                {ETICHETTE_PERIODICITA[p]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-[158px] flex-1 flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Da quando
          </span>
          <input
            type="month"
            value={dal}
            onChange={(e) => setDal(e.target.value)}
            className="min-w-0 rounded-field bg-surface px-2.5 py-2 text-[14px] text-ink outline-none"
          />
        </label>
      </div>

      <p className="text-[12px] text-ink-faint">
        Metti la data da cui vale davvero, anche se è passata: serve a far
        tornare i mesi già andati, non solo quelli che verranno.
      </p>

      <div className="flex justify-end gap-2">
        <Pill variant="ghost" size="sm" onClick={onAnnulla}>
          Annulla
        </Pill>
        <Pill variant="primary" size="sm" onClick={salva} loading={salvando}>
          Salva
        </Pill>
      </div>
    </div>
  );
}
